import React, { useEffect, useState, useRef, useCallback } from "react";
import { loadAirwallex, createElement } from "airwallex-payment-elements";
import { generalMessageTemplate } from "../entities/generalMessageTemplate";
import { sendMessage, addListener, removeListener } from "../services/websocketManager";
import { useTranslation } from "react-i18next";
import { LottieAnimation } from "../lottie/LottieAnimation.js";
import {
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";

const DropIn = ({
  amount,
  currency = "USD",
  env,
  onSuccess = () => {},
  onError = () => {},
  onReady = () => {},
  style = {},
  className = "",
  language,
  planId 
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [clientSecret, setClientSecret] = useState(null);
  const [intentId, setIntentId] = useState(null);
  const [elementReady, setElementReady] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [, setIsLoading] = useState(true);
  const dropinElementRef = useRef(null);
  const mountedRef = useRef(false);

  // Memoized WebSocket event handler
  const handleWebSocketEvent = useCallback((event) => {
    if (
      event.type === "message" &&
      event.message.messageType === "airwallex_client_secret"
    ) {
      const { clientSecret, intentId } = event.message.payment;
      setClientSecret(clientSecret);
      setIntentId(intentId);
      setIsLoading(false);
    }
  }, []);

  // Initialize payment intent via WebSocket
  useEffect(() => {
    addListener(handleWebSocketEvent);

    const msg = { ...generalMessageTemplate };
    msg.messageType = "create_payment_intent";
    msg.payment.amount = amount;
    msg.payment.currency = currency;
    msg.payment.planId = planId;
    sendMessage(msg);

    return () => {
      removeListener(handleWebSocketEvent);
    };
  }, [amount, currency, handleWebSocketEvent, planId]);

  // Initialize Airwallex and create drop-in element
  useEffect(() => {
    if (!clientSecret || !intentId || mountedRef.current) return;

    const initializeAirwallex = async () => {
      try {
        await loadAirwallex({
          env,
          origin: window.location.origin,
          locale: language
        });

        const dropinElement = createElement("dropIn", {
          intent_id: intentId,
          client_secret: clientSecret,
          currency,
          locale: language
        });

        if (dropinElement?.mount) {
          dropinElement.mount("dropin");
          dropinElementRef.current = dropinElement;
          mountedRef.current = true;
          setErrorMessage(""); // Clear any previous errors
        } else {
          throw new Error("Failed to create drop-in element");
        }
      } catch (error) {
        console.error("Airwallex initialization error:", error);
        setErrorMessage(t("error"));
        onError(error.message || t("error"));
      }
    };

    initializeAirwallex();
  }, [clientSecret, intentId, currency, env, language, onError, t]);

  // Event handlers
  const handleReady = useCallback(() => {
    setElementReady(true);
    onReady();
  }, [onReady]);

  const handleSuccess = useCallback((e) => {
    onSuccess(e.detail);
  }, [onSuccess]);

  const handleError = useCallback((e) => {
    const msg = e?.detail?.error?.message || t("error");
    setErrorMessage(msg);
    onError(msg);
  }, [onError, t]);

  // Set up event listeners
  useEffect(() => {
    window.addEventListener("onReady", handleReady);
    window.addEventListener("onSuccess", handleSuccess);
    window.addEventListener("onError", handleError);

    return () => {
      window.removeEventListener("onReady", handleReady);
      window.removeEventListener("onSuccess", handleSuccess);
      window.removeEventListener("onError", handleError);
    };
  }, [handleReady, handleSuccess, handleError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (dropinElementRef.current && dropinElementRef.current.unmount) {
        try {
          dropinElementRef.current.unmount();
        } catch (error) {
          console.warn("Error unmounting Airwallex element:", error);
        }
      }
    };
  }, []);

  // Responsive container styles
  const getContainerStyle = () => {
    const baseWidth = isSmallMobile ? "540px" : isMobile ? "540px" : "540px";
    const basePadding = isSmallMobile ? "10px" : isMobile ? "15px" : "20px";
    const baseMargin = isMobile ? "20px auto" : "40px auto";
    
    return {
      width: baseWidth,
      maxWidth: isMobile ? "120%" : "540px",
      margin: baseMargin,
      padding: basePadding,
      display: elementReady ? "block" : "none",
      ...style,
    };
  };

  // Responsive loading container styles
  const getLoadingContainerStyle = () => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: isSmallMobile ? "8px" : "10px",
    minHeight: isMobile ? "250px" : "300px",
    width: "100%",
    padding: isMobile ? "10px" : "20px",
  });

  return (
    <div className={className} style={{ width: "100%", maxWidth: "100%" }}>
      {!errorMessage && !elementReady && (
        <div style={getLoadingContainerStyle()}>
          <Typography
            variant={isMobile ? "h6" : "h5"}
            gutterBottom
            sx={{ 
              color: "#4a707d", 
              fontWeight: "bold",
              textAlign: "center",
              fontSize: isSmallMobile ? "1.1rem" : isMobile ? "1.25rem" : "1.5rem"
            }}
          >
            {t("Payment_Component")}
          </Typography>

          <LottieAnimation
            path="https://res.cloudinary.com/dnlgtek2a/raw/upload/v1743418492/Lottie/PaymentWait.json"
            width={isSmallMobile ? 120 : isMobile ? 150 : 200}
            height={isSmallMobile ? 120 : isMobile ? 150 : 200}
            autoplay
            loop
          />
        </div>
      )}
      
      {errorMessage && (
        <div 
          role="alert" 
          style={{ 
            color: "red", 
            marginBottom: "20px",
            padding: isMobile ? "10px" : "15px",
            textAlign: "center",
            fontSize: isSmallMobile ? "0.85rem" : "1rem",
            backgroundColor: "#ffebee",
            borderRadius: "8px",
            border: "1px solid #ffcdd2"
          }}
        >
          {errorMessage}
        </div>
      )}
      
      <div
        id="dropin"
        style={getContainerStyle()}
        aria-live="polite"
        aria-label={t("payment_form_label", "Payment form")}
      />
    </div>
  );
};

export default DropIn;